import { apiGet, apiPost, apiPostForm, apiPut, apiPatch } from './apiClient';

export async function getWarehouseListing({ page = 1, limit = 10, stateId, cityId, warehouseId, search } = {}) {
  const params = {
    page,
    limit,
    ...(stateId ? { state_id: stateId } : {}),
    ...(cityId ? { city_id: cityId } : {}),
    ...(warehouseId ? { warehouse_id: warehouseId } : {}),
    ...(search ? { search } : {})
  };
  return apiGet('/wms/warehouses', { params });
}

export async function getWarehouseStates() {
  return apiGet('/wms/warehouses/states');
}

export async function getMapApiKey() {
  return apiGet('/wms/warehouses/map-keys?platform=driverWeb');
}

let googleMapsPromise = null;

export function getGoogleMapsScript(apiKey) {
  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.onload = resolve;
      return;
    }

    const script = document.createElement("script");

    script.id = "google-maps-script";

    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;

    script.async = true;
    script.defer = true;

    script.onload = resolve;

    script.onerror = reject;

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export async function getWarehouseCities(stateId) {
  return apiGet('/wms/warehouses/cities', { params: stateId ? { state_id: stateId } : {} });
}

export async function getWarehouseLocationByPostalCode(postalCode) {
  return apiGet('/wms/warehouses/postal-code', {
    params: postalCode ? { postal_code: postalCode } : {}
  });
}

export async function createWarehouse(payload) {
  return apiPost('/wms/warehouses', payload);
}

export async function updateWarehouse(warehouseId, payload) {
  return apiPut(`/wms/warehouses/${warehouseId}`, payload);
}

export async function updateWarehouseStatus(warehouseId, status) {
  return apiPatch(`/wms/warehouses/${warehouseId}/status`, { status: Boolean(status) });
}

export async function uploadWarehouseImages(files = []) {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('images', file);
  });
  return apiPostForm('/wms/upload/images', formData);
}

export async function getChamberListing(warehouseId, { page = 1, limit = 10, search } = {}) {
  return apiGet(`/wms/warehouses/${warehouseId}/chambers`, {
    params: {
      page,
      limit,
      ...(search ? { search } : {})
    }
  });
}

export async function createChamber(warehouseId, payload) {
  return apiPost(`/wms/warehouses/${warehouseId}/chambers`, payload);
}

export async function getEmployeesByWarehouseId(warehouseId, { page = 1, limit = 10 } = {}) {
  return apiGet('/wms/users/getAllPersonByWarehouseId', { params: { warehouse_id: warehouseId, page, limit } });
}

export async function createEmployeePerson(payload) {
  return apiPost('/wms/users/persons', payload);
}

export async function getUserRoles() {
  return apiGet('/wms/users/roles');
}

export async function getUserRoleTypes() {
  return apiGet('/wms/users/role-types');
}

export async function submitChamberGridLayout(warehouseId, chamberId, payload) {
  return apiPost(`/wms/warehouses/${warehouseId}/chambers/${chamberId}/grid`, payload);
}

export async function getChamberCoordinates(warehouseId, chamberId) {
  return apiGet(`/wms/warehouses/${warehouseId}/chambers/${chamberId}/coordinates`);
}

export async function getChamberInventory(warehouseId, chamberId, { clusterId, coordinateId, coordinate } = {}) {
  const params = {};
  if (Number.isFinite(Number(clusterId)) && Number(clusterId) > 0) {
    params.cluster_id = Number(clusterId);
  } else if (Number.isFinite(Number(coordinateId)) && Number(coordinateId) > 0) {
    params.coordinate_id = Number(coordinateId);
  } else if (coordinate) {
    params.coordinate = String(coordinate).trim();
  }
  return apiGet(`/wms/warehouses/${warehouseId}/chambers/${chamberId}/inventory`, { params });
}

export async function submitWarehouseLayout(warehouseId, payload) {
  return apiPost(`/wms/warehouses/${warehouseId}/layout`, payload);
}

export async function getWarehouseLayout(warehouseId) {
  return apiGet(`/wms/warehouses/${warehouseId}/layout`);
}

export async function getWarehouseDashboard({ warehouseId, startDate, endDate }) {
  return apiGet('/common/dashboard/warehouse-dashboard', {
    params: {
      warehouse_id: warehouseId,
      start_date: startDate,
      end_date: endDate
    }
  });
}

export async function getChamberOccupancyByWarehouse({ warehouseId, startDate, endDate } = {}) {
  return apiGet('/common/dashboard/chamber-occupancy-by-warehouse', {
    params: {
      ...(warehouseId ? { warehouse_id: warehouseId } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {})
    }
  });
}